import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {BaseItemObject, CopyableItemStyles, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {LinePointsStyle, LineDash} from '@PosterWhiteboard/items/line-item/pmw-fabric-line';
import type {CustomControl, OnResizeParams} from '@PosterWhiteboard/poster/poster-item-controls';
import {renderControl} from '@PosterWhiteboard/poster/poster-item-controls';
import {Control, controlsUtils, Polyline, type TPointerEvent, type Transform} from '@postermywall/fabricjs-2';
import type {DeepPartial} from '@/global';

export interface LineItemObject extends BaseItemObject {
  lineDash: LineDash;
  lineEnd: LinePointsStyle;
  lineStart: LinePointsStyle;
}

export class LineItem extends Item {
  declare _fabricObject: Polyline;
  public gitype: ITEM_TYPE.LINE = ITEM_TYPE.LINE;
  public thickness = 4;
  public lineDash: LineDash = LineDash.NONE;
  public lineEnd: LinePointsStyle = LinePointsStyle.NONE;
  public lineStart: LinePointsStyle = LinePointsStyle.NONE;

  get fabricObject(): Polyline {
    return this._fabricObject;
  }

  public async isPDFSafe(): Promise<boolean> {
    return true;
  }

  public getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      border: this.border.toObject(),
      textBorder: this.border.toObject(),
      lineDash: this.lineDash,
      lineEnd: this.lineEnd,
      lineStart: this.lineStart,
    };
  }

  public async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<LineItemObject> = {};
    if (copiedStyles.textBorder !== undefined) {
      stylesObj.border = this.getBorderFromCopiedStyles(copiedStyles.textBorder);
    }
    if (copiedStyles.lineDash !== undefined) {
      stylesObj.lineDash = copiedStyles.lineDash;
    }
    if (copiedStyles.lineEnd !== undefined) {
      stylesObj.lineEnd = copiedStyles.lineEnd;
    }
    if (copiedStyles.lineStart !== undefined) {
      stylesObj.lineStart = copiedStyles.lineStart;
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['scaleX', 'scaleY']);
    await this.updateFromObject({...commonStylesObj, ...stylesObj});
  }

  public hasClickableLink(): boolean {
    return false;
  }

  public toObject(): LineItemObject {
    return {
      ...super.toObject(),
      lineDash: this.lineDash,
      lineEnd: this.lineEnd,
      lineStart: this.lineStart,
    };
  }

  private getLineMLControl(onResize: (params: OnResizeParams) => void): CustomControl {
    const key = 'lineMl';
    return {
      key,
      control: new Control({
        x: -0.5,
        y: 0,
        visible: false,
        cursorStyleHandler: (): string => {
          return 'crosshair';
        },
        actionName: 'resize',
        render: renderControl.bind(this, key),
        actionHandler: (e: TPointerEvent, transformData: Transform): boolean => {
          if (!transformData.corner) {
            return false;
          }

          onResize({e, delta: 5});
          return true;
        },
      }),
    };
  }

  private getLineMrControl(onResize: (params: OnResizeParams) => void): CustomControl {
    const key = 'lineMr';
    return {
      key,
      control: new Control({
        x: 0.5,
        y: 0,
        visible: false,
        cursorStyleHandler: (): string => {
          return 'crosshair';
        },
        actionName: 'resize',
        render: renderControl.bind(this, key),
        actionHandler: (e: TPointerEvent, transformData: Transform): boolean => {
          if (!transformData.corner) {
            return false;
          }

          onResize({e, delta: 5});
          return true;
        },
      }),
    };
  }

  protected async getFabricObjectForItem(): Promise<Polyline> {
    const obj = new Polyline(
      [
        {x: 3, y: 30},
        {x: 300, y: 30},
      ],
      {
        hasBorders: true,
        strokeWidth: 1,
        stroke: 'grey',
      }
    );
    obj.set({
      controls: controlsUtils.createPolyControls(obj),
    });
    obj.set(this.getCommonOptions());
    return obj;
  }

  public async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();
    this.fabricObject.set({
      fill: 'red',
      thickness: this.thickness,
      strokeWidth: this.thickness,
      stroke: 'grey',
      lineDash: this.lineDash,
      lineEnd: this.lineEnd,
      lineStart: this.lineStart,
    });
  }
}
